/**
 * Audit logs access utilities.
 * Feature-specific wrapper for audit log access checks.
 */

import type { AugmentedAccount } from '@/services/auth/types';
import { PlanFeature } from '@/services/auth/types';
import { hasUnlimitedPlanFeatureAccess } from '@/services/auth/plan-features';

/**
 * Check if an account has unlimited audit log access based on plan features.
 * Uses the generic PlanFeature infrastructure for consistency with other feature checks.
 * Accounts with unlimited access (limit === false or limit === 0) or premium accounts have no date restrictions.
 * This matches the backend logic in services which uses gated_features to determine limits.
 *
 * @param account - The augmented account to check (must have planFeatures populated)
 * @returns true if the account has unlimited audit log access, false otherwise
 */
export function hasUnlimitedAuditLogAccess(account: AugmentedAccount): boolean {
  return hasUnlimitedPlanFeatureAccess(account, PlanFeature.AuditLogs);
}

/**
 * Get the audit log day limit from the account's plan features.
 * Returns the actual limit from backend, or null if unlimited.
 * This matches the backend logic in services which uses gated_features to determine limits.
 *
 * @param account - The augmented account to check (must have planFeatures populated)
 * @returns The day limit (number), or null if unlimited. Returns 30 as fallback if feature not enabled.
 */
export function getAuditLogDayLimit(account: AugmentedAccount): number | null {
  // Premium accounts have no limit
  if (account.premium) {
    return null;
  }

  const auditLogsFeature = account.planFeatures[PlanFeature.AuditLogs];

  // If feature not enabled or not found, default to limited access (30 days as fallback)
  if (auditLogsFeature == null || !auditLogsFeature.enabled) {
    return 30; // Default fallback
  }

  // limit === false or limit === 0 means unlimited
  if (auditLogsFeature.limit === false || auditLogsFeature.limit === 0) {
    return null;
  }

  // Return the actual numeric limit from backend
  return auditLogsFeature.limit;
}
